import { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";

import { get, put } from "lib/api";
import { useAuth } from "context/AuthContext";
import MobileAddressManager from "mobile/components/MobileAddressManager";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={800} sx={{ textAlign: "right", wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};
Row.defaultProps = {
  value: "—",
};

export default function CustomerDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeCompany } = useAuth();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["m-customer", documentId],
    queryFn: () => get(`/api/customers/${documentId}`, { populate: "*" }),
    enabled: !!documentId,
  });

  const customer = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);

  const emails = Array.isArray(customer?.emails) ? customer.emails.filter(Boolean) : [];
  const phones = Array.isArray(customer?.phones) ? customer.phones.filter(Boolean) : [];
  const addresses = Array.isArray(customer?.addresses) ? customer.addresses : [];
  const priceTier = String(customer?.priceTier || "retail");
  const saveAddressesMutation = useMutation({
    mutationFn: (nextAddresses) => put(`/api/customers/${customer?.documentId || documentId}`, { data: { addresses: nextAddresses } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m-customer", documentId] }),
  });

  return (
    <IosPage>
      <MobileTopBar
        title={customer?.name || "Customer"}
        subtitle={customer?.accountNumber ? `Acct ${customer.accountNumber}` : "Details"}
        onBack={() => navigate("/m/customers")}
        right={
          <IconButton aria-label="Refresh" size="small" onClick={() => qc.invalidateQueries({ queryKey: ["m-customer", documentId] })}>
            <Icon fontSize="small">refresh</Icon>
          </IconButton>
        }
      />

      {(isLoading || isFetching) && <LinearProgress sx={{ mt: 1 }} />}

      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error?.response?.status === 404 ? "Customer not found." : error?.message || "Error loading customer."}
        </Typography>
      ) : null}

      {customer ? (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <IosCard>
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={900} noWrap>
                    {customer.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {customer.contact || "—"}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={priceTier.toUpperCase()}
                  variant="outlined"
                  color={priceTier === "wholesale" ? "info" : priceTier === "bulk" ? "warning" : "default"}
                  sx={{ fontWeight: 900 }}
                />
              </Stack>

              <Divider />

              <Row label="Account #" value={customer.accountNumber || "—"} />
              <Row label="Contact" value={customer.contact || "—"} />
              <Row label="Emails" value={emails.length ? emails.join(", ") : "—"} />
              <Row label="Phones" value={phones.length ? phones.join(", ") : "—"} />
            </Stack>
          </IosCard>

          <MobileAddressManager
            addresses={addresses}
            companyKey={activeCompany}
            disabled={saveAddressesMutation.isPending}
            onSave={(nextAddresses) => saveAddressesMutation.mutateAsync(nextAddresses)}
          />
        </Stack>
      ) : null}
    </IosPage>
  );
}
